import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useState } from "react";

import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { useArticleCommentLink } from "~/lib/hooks";
import { parseQueryString } from "~/lib/utils";

import { CloseCommentEvent } from "./consts";
import { CommentContainer, StyledDrawer, StyledIFrame } from "./styles";

export const ArticleCommentsDrawer: FunctionComponent = () => {
  const [showComments, setShowComments] = useState(false);
  const { query } = useRouter();
  const [iframeUrl, setIframeUrl] = useState("");
  const { isCommentLink, toggleByUrl: toggleCommentByUrl } = useArticleCommentLink();
  const { openLoginDialog } = useLoginDialogStateHelper();

  useEffect(() => {
    setShowComments(isCommentLink());
  }, [isCommentLink]);

  useEffect(() => {
    const entityId = parseQueryString(query.entityId);
    if (!showComments || !entityId) return;

    const commentUrl = new URL(location.origin + `/scmp_comments/popup/${entityId}`);
    const commentId = parseQueryString(query.commentId);

    if (commentId) {
      commentUrl.searchParams.set("commentID", commentId);
    }

    setIframeUrl(commentUrl.toString());
  }, [showComments, query]);

  const handleCloseDrawer = useCallback(() => {
    toggleCommentByUrl();
  }, [toggleCommentByUrl]);

  useEffect(() => {
    const handleCloseCommentEvent = (event: Event) => {
      if (event.data.event === CloseCommentEvent) {
        handleCloseDrawer();
      }
    };

    window.addEventListener("message", handleCloseCommentEvent);

    return () => {
      window.removeEventListener("message", handleCloseCommentEvent);
    };
  }, [handleCloseDrawer]);

  useEffect(() => {
    const handler = (event: MessageEvent<{ event: string; url: string }>) => {
      if (event.data.event === "openLoginLayer") {
        openLoginDialog({
          description:
            "Login or register to join in the discussion, plus read what others have to say.",
          destination: event.data.url,
          ga4CustomParameter: {
            trigger_point: "comment",
          },
          title: "Contribute to the conversation today",
        });
      }
    };

    window.addEventListener("message", handler);

    return () => {
      window.removeEventListener("message", handler);
    };
  });

  return (
    <StyledDrawer
      anchor="right"
      open={showComments && iframeUrl !== ""}
      onClose={handleCloseDrawer}
    >
      <CommentContainer>
        <StyledIFrame src={iframeUrl} />
      </CommentContainer>
    </StyledDrawer>
  );
};

ArticleCommentsDrawer.displayName = "ArticleCommentsDrawer";

type Event = {
  data: {
    event: string;
  };
};
